/**
 * Bank Entity Service - Risk Assessment
 *
 * Handles risk evaluation and management including:
 * - Community risk profiling
 * - Collateral assessment
 * - Credit analysis
 * - Portfolio risk monitoring
 */

import { LoanRiskAssessment, CollateralAsset, RiskLevel } from '@/types/entities/bank';

// ====== RISK ASSESSMENT INTERFACES ======

export interface RiskAssessmentFilters {
  riskLevel?: RiskLevel[];
  communityId?: string;
  dateRange?: {
    start: string;
    end: string;
  };
}

// ====== RISK ASSESSMENT SERVICE ======

class BankRiskAssessmentService {
  private baseUrl = '/api/bank/risk';
  private useMockApi = import.meta.env.VITE_USE_MOCK_API === 'true';
  private isDevelopment = import.meta.env.DEV || import.meta.env.NODE_ENV === 'development';

  /**
   * Get risk assessment for proposal
   */
  async getRiskAssessment(proposalId: string): Promise<LoanRiskAssessment> {
    if (this.useMockApi || this.isDevelopment) {
      return this.getMockRiskAssessment();
    }

    try {
      const response = await fetch(`${this.baseUrl}/assessment/${proposalId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.warn('Risk assessment API not available, using mock data');
        return this.getMockRiskAssessment();
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching risk assessment:', error);
      return this.getMockRiskAssessment();
    }
  }

  /**
   * Assess collateral value
   */
  async assessCollateral(assets: CollateralAsset[]): Promise<unknown> {
    if (this.useMockApi || this.isDevelopment) {
      return this.getMockCollateralAssessment();
    }

    try {
      const response = await fetch(`${this.baseUrl}/collateral`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ assets }),
      });

      if (!response.ok) {
        throw new Error(`Failed to assess collateral: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error assessing collateral:', error);
      return this.getMockCollateralAssessment();
    }
  }

  private getMockRiskAssessment(): LoanRiskAssessment {
    return {
      overallRisk: 'medium',
      riskScore: 72,
      riskFactors: [],
      mitigatingFactors: [],
      recommendations: [],
      stressTestResults: [],
      regulatoryConsiderations: [],
    };
  }

  private getMockCollateralAssessment(): unknown {
    return {
      totalValue: 525000,
      assessedValue: 472500,
      coverageRatio: 1.58,
      riskAdjustments: [],
    };
  }
}

export const bankRiskAssessmentService = new BankRiskAssessmentService();
